<template>
  <div>
    <v-navigation-drawer
      v-model="panel.activated"
      :width="$isMobile ? '100%' : '60%'"
      right
      fixed
      :permanent="panel.activated"
      hide-overlay
      class="z-index--100">
      <div class="ma-3">
        <div class="d-flex justify-space-between">
          <div class="d-flex">
            <v-btn
              dark
              fab
              x-small
              outlined
              color="primary"
              class="mr-3"
              @click="closeDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <div
              class="subtitle-1 primary--text mr-3">
              Orden de compra: {{ detail.uuid }}
            </div>
          </div>
          <div>
            <v-tooltip
            v-if="purchaseOrderPermissions['print-purchase-order']"
            left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  x-small
                  depressed
                  @click="printOrder"
                >
                  <v-icon
                    small
                  >
                    mdi-printer
                  </v-icon>
                </v-btn>
              </template>
              <span>Imprimir orden de compras</span>
            </v-tooltip>
            <v-tooltip
            v-if="purchaseOrderPermissions['send-purchase-order-via-email']"
            left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  x-small
                  depressed
                  @click="openSendByEmailDialog"
                >
                  <v-icon
                    small
                  >
                    mdi-email
                  </v-icon>
                </v-btn>
              </template>
              <span>Enviar orden de compra por mail</span>
            </v-tooltip>
            <v-tooltip
              v-if="purchaseOrderPermissions['copy-purchase-order-link']"
              left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  x-small
                  depressed
                  v-clipboard:copy="copyToLink"
                  v-clipboard:success="onCopy"
                >
                  <v-icon
                    small
                  >
                    mdi-link-variant
                  </v-icon>
                </v-btn>
              </template>
              <span>Copy link</span>
            </v-tooltip>
          </div>
        </div>
        <v-container ref="order-detail">
          <v-row>
            <v-col md="12">
              <v-card outlined>
                <v-row>
                  <v-col md="6" cols="12">
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        <div>
                          Restaurante:
                        </div>
                        <div class="grey--text">
                          {{ order.restaurant }}
                        </div>
                      </div>
                    </div>
                    <!-- <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Nombre del emisor:
                      </div>
                      <div class="grey--text">
                        {{ detail.author && detail.author.email ? detail.author.email : '' }}
                      </div>
                    </div> -->
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        {{ displayModificationText(detail) }}
                      </div>
                      <div class="grey--text">
                        {{ getLastModificationPerson(detail) }}
                      </div>
                    </div>
                    <div class="subtitle-2 mb-2 px-2" v-if="detail.status === 'canceled' && detail.cancelationMotive">
                      <div>
                        Motivo de cancelación:
                      </div>
                      <div class="grey--text">
                        {{ findCancelationMotive(detail.cancelationMotive) }}
                      </div>
                    </div>
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Estatus:
                      </div>
                      <div :class="`${statusColors(detail.status)}--text`">
                        {{ statusNames(detail.status) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col md="3" cols="12">
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        RFC:
                      </div>
                      <div v-if="detail.restaurant && detail.restaurant.taxInformation" class="grey--text">
                        {{ detail.restaurant.taxInformation.taxId }}
                      </div>
                    </div>
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Fecha de emisión:
                      </div>
                      <div class="grey--text">
                        {{ convertToDateString(detail.createdAt) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col md="3" cols="12">
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Nombre fiscal:
                      </div>
                      <div v-if="detail.restaurant && detail.restaurant.taxInformation" class="grey--text">
                        {{ detail.restaurant.taxInformation.taxName }}
                      </div>
                    </div>
                    <div class="subtitle-2 mb-2 px-2">
                      <v-tooltip class="style-tool" left>
                        <template v-slot:activator="{ on, attrs }">
                          <span
                            v-bind="attrs"
                            v-on="on"
                            class="font-weight-bold"
                          >
                            <v-icon small>mdi-information</v-icon>
                            Histórico por estatus
                          </span>
                        </template>
                        <v-timeline>
                          <v-timeline-item
                            small
                            v-for="(item, index) in detail.statusChanges"
                            :key="`item-${index}`"
                          >
                            <strong>{{ statusNames(item.name) }}</strong>
                            <div style="font-size: 10px;">
                              {{ item.createdAt | toDateTime }}
                            </div>
                          </v-timeline-item>
                        </v-timeline>
                      </v-tooltip>
                    </div>
                  </v-col>
                </v-row>
                <div class="d-flex justify-space-between px-2 subtitle-2">
                  <span
                    class="font-weight-bold mb-2">
                    Subtotal:
                  </span>
                  <span class="title">${{ subtotal | toFix2 | numberWithComma }}</span>
                </div>
                <div class="d-flex justify-space-between px-2 subtitle-2" v-if="['invoiced', 'received', 'pending_payment', 'paid'].includes(detail.status)">
                  <span
                    class="font-weight-bold mb-2">
                    ISR:
                  </span>
                  <span class="title" >${{ (isr) | toFix2 | numberWithComma }}</span>
                </div>
                <div class="d-flex justify-space-between px-2 subtitle-2" v-if="['invoiced', 'received', 'pending_payment', 'paid'].includes(detail.status)">
                  <span
                    class="font-weight-bold mb-2">
                    IVA:
                  </span>
                  <span class="title" >${{ iva > 0 ? iva : IVAFromProducts | toFix2 | numberWithComma }}</span>
                </div>
                <div class="d-flex justify-space-between px-2 subtitle-2" v-if="['invoiced', 'received', 'pending_payment', 'paid'].includes(detail.status)">
                  <span
                    class="font-weight-bold mb-2">
                    IEPS:
                  </span>
                  <span class="title">${{ ieps > 0 ? ieps : IEPSFromProducts | toFix2 | numberWithComma }}</span>
                </div>
                <div class="d-flex justify-space-between px-2 subtitle-2">
                  <span
                    class="font-weight-bold mb-2">
                    Total de la orden (con impuestos):
                  </span>
                  <span class="title">${{ totalToPay | toFix2 | numberWithComma }}</span>
                </div>
              </v-card>

              <v-btn
                v-on:click="onDeliverDialog"
                block
                color="success"
                class="my-3"
                v-if="$isMobile && ['invoiced'].includes(detail.status)">
                Entregar mercancía
              </v-btn>

              <v-card outlined class="mt-5">
                <div class="d-flex justify-space-between px-2 subtitle-2">
                  <div class="subtitle-1 px-2">
                  Total de facturas:
                </div>
                  <span class="title">${{ totalInvoices | toFix2 | numberWithComma }}</span>
                </div>
                <div class="pa-2">
                  <ul v-if="countryRestaurant === 'MX'">
                    <li v-for="(item, index) in detail.invoices" :key="item._id">
                      <v-divider v-if="index > 0"/>
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <v-menu offset-y right>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                x-large
                                v-on="on"
                                plain
                                color="primary"
                                class="pa-0"
                              >
                                {{ item.uuid }}
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item @click="viewInvoice(item)">
                                <v-list-item-title>
                                  Visualizar factura
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="downloadInvoice(item)">
                                <v-list-item-title>
                                  Descargar factura
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item v-if="item.status === 'active' && ['facturapi', 'xml'].includes(item.source) && ['invoiced', 'received', 'pending_payment', 'paid'].includes(order.status)" @click="checkStatusInvoice(item)">
                                <v-list-item-title>
                                  Verificar estatus en el SAT
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item
                                v-if="item.status === 'active' && ['facturapi', 'xml'].includes(item.source) && !['received', 'paid', 'pending_payment'].includes(order.status) && purchaseOrderPermissions['change-purchase-order-cancel-invoice']"
                                @click="openCancelInvoiceDialog(item)">
                                <v-list-item-title>
                                  <span v-if="item.source === 'facturapi'">Cancelar factura</span>
                                  <span v-if="item.source === 'xml'">Desvincular factura</span>
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item :href="downloadOrderInvoice" v-if="['received', 'pending-payment', 'paid'].includes(order.status)">
                                <v-list-item-title>
                                  Descargar como PDF
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                          <div class="caption">
                            Folio: <span class="primary--text">{{ item.folio }}</span>
                          </div>
                          <div :class="[`${invoiceStatuses[item.status].color}--text`, 'caption']">
                            {{ invoiceStatuses[item.status].text }}
                          </div>
                          <div class="caption">
                            Origen: <span class="primary--text"> {{ invoiceSource[item.source] }}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </v-card>

              <v-card outlined class="mt-5" v-if="countryRestaurant === 'MX'">
                <div class="subtitle-1 px-2">
                  Nota de crédito:
                </div>
                <div class="pa-2">
                  <ul>
                    <li v-for="(item, index) in detail.creditNotes" :key="item._id">
                      <v-divider v-if="index > 0"/>
                      <div class="d-flex justify-space-between align-center pa-2">
                        <div>
                          <div>
                            <a class="primary--text" href="#" @click.prevent="viewCreditNote(item)">{{ item.uuid }}</a>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </v-card>
            </v-col>

            <v-col md="12" v-if="!['created'].includes(detail.status) && ['provider', 'provider-buyer'].includes($is())">
              <v-card outlined class="pa-3">
                <div class="body-2 mb-3">
                  Notas del restaurante:
                </div>
                <div>
                  {{ detail.notes }}
                </div>
              </v-card>
            </v-col>

            <v-col md="12" v-if="['invoiced',
              'received',
              'pending_payment',
              'paid', 'invalid'].includes(detail.status) && tableOriginalProducts.total > 0">
              <v-card outlined>
                <div class="d-flex justify-end">
                  <div class="font-weight-bold ma-2">
                    Cantidad de artículos:
                    <span v-bind:class="{'red--text': quantityOriginalProducts > 0 && quantityOriginalProducts !== totalProducts}">
                      {{ quantityOriginalProducts }}
                    </span>
                  </div>
                  <div class="font-weight-bold ma-2">
                    Total de compra:
                    <span v-bind:class="{'red--text': totalOriginalProducts > 0 && totalOriginalProducts !== total}">
                      ${{ totalOriginalProducts | toFix2 | numberWithComma }}
                    </span>
                  </div>
                </div>
                <v-list>
                  <v-list-group
                    v-for="item in items"
                    :key="item.title"
                    v-model="item.active"
                    no-action
                  >
                    <template v-slot:activator>
                      <v-list-item-content>
                        <v-list-item-title v-text="item.title"></v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <v-list-item>
                      <v-list-item-content>
                        <v-data-table
                          :loading-text="tableOriginalProducts.title"
                          :server-items-length="tableOriginalProducts.total"
                          :loading="tableOriginalProducts.loading"
                          :headers="getHeaders"
                          :items="tableOriginalProducts.data"
                          :options="tableOriginalProducts.options"
                          sort-by="product"
                          :height="tableOriginalProducts.data.length >= 10 ? 600 : 400"
                          hide-default-footer>
                          <template v-slot:item.amount="{ item }">
                            <div class="text-right">
                              ${{ (item.amount || 0) | toFixNumber(2) | numberWithComma }}
                            </div>
                          </template>
                          <template v-slot:item.unit.name="{ item }">
                            <div>
                              {{ item.unit.name }}
                            </div>
                            <div v-if="item.unit._id !== item.contentUnit" class="grey--text">
                              de {{ item.contentQuantity }} {{ getUnitName(item.contentUnit) }} c/u
                            </div>
                          </template>
                          <template v-slot:item.amountWithTax="{ item }">
                            <div class="text-right">
                              ${{ (item.amountWithTax || 0) | toFixNumber(2) | numberWithComma }}
                            </div>
                          </template>
                          <template v-slot:item.quantityConfirmed="{ item }">
                            <div class="text-right">
                              {{ item.quantityConfirmed }}
                            </div>
                          </template>
                          <template
                            v-slot:item.quantityReceived="{ item }"
                            v-if="(detail.status === 'invoiced' || (detail.status === 'confirmed' && (detail.provider.receiveWithoutInvoice || countryRestaurant !== 'MX'))) && ['manager', 'stockmanager'].includes($is())">
                            {{ item.quantityReceived }}
                          </template>
                          <template v-slot:item.totalWithTax="{ item }">
                            ${{ (item.totalWithTax || 0) | toFixNumber(2) | numberWithComma }}
                          </template>
                          <template v-slot:item.total="{ item }">
                            ${{ (item.total || 0) | toFixNumber(2) | numberWithComma }}
                          </template>
                        </v-data-table>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-group>
                </v-list>
              </v-card>
            </v-col>

            <v-col md="12">
              <div v-if="detail.status === 'sent' && detail.isRead">
                <div class="py-2">
                  <v-alert dense type="info">
                    Asegurate de agregar servicios si son necesarios, una vez confirmada la orden de compra ya no podrás agregarlos.
                  </v-alert>
                  <v-alert v-if="actualDate" dense type="warning">
                    <span>Es posible que tu pago sufra un <b>retraso</b> debido a que estás facturando mismo día en que se pagan a proveedores por parte de Eato.</span>
                  </v-alert>
                </div>
                <v-tabs v-model="tab">
                  <v-tab
                    v-if="purchaseOrderPermissions['change-purchase-order-add-product']"
                    key="Productos">
                    Productos
                  </v-tab>
                  <v-tab
                    v-if="purchaseOrderPermissions['change-purchase-order-add-service']"
                    key="Servicios">
                    Servicios
                  </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                  <v-tab-item key="Productos">
                    <div class="d-flex justify-end">
                      <div class="font-weight-bold mb-2">
                        Total de artículos: {{ totalProducts }}
                      </div>
                    </div>
                    <v-data-table
                      :loading-text="table.title"
                      :server-items-length="table.total"
                      :loading="table.loading"
                      :headers="getHeaders"
                      :items="table.data"
                      :options="table.options"
                      sort-by="product"
                      class="elevation-1"
                      fixed-header
                      :height="table.data.length >= 10 ? 600 : 400"
                      hide-default-footer>
                      <template
                        v-slot:item.amount="{ item }"
                        v-if="detail.status === 'sent' && detail.isRead && purchaseOrderPermissions['change-purchase-order-update-product-price']">
                        <div class="d-flex justify-space-between">
                          <v-edit-dialog
                            :return-value.sync="item.amount"
                            large
                            dark
                            persistent
                            @save="save(item)"
                            cancel-text="Cancelar"
                            save-text="Guardar">
                            ${{ (item.amount || 0) | toFixNumber(2) }}
                            <v-icon light small>mdi-border-color</v-icon>
                            <template v-slot:input>
                              <v-text-field
                                v-model.number="item.amount"
                                label="Edit"
                                single-line>
                              </v-text-field>
                            </template>
                          </v-edit-dialog>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                v-on="on"
                                icon
                                color="secondary"
                                x-small
                                depressed
                                @click="updatePrice(item)"
                              >
                                <v-icon small>
                                  mdi-autorenew
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>Actualizar precio</span>
                          </v-tooltip>
                        </div>
                      </template>
                      <template v-else v-slot:item.amount="{ item }">
                        <div class="text-right">
                          ${{ (item.amount || 0) | toFixNumber(2) | numberWithComma }}
                        </div>
                      </template>
                      <template v-slot:item.quantityConfirmed="{ item }">
                        <v-edit-dialog v-if="detail.status === 'sent' && detail.status !== 'confirmed' && detail.isRead && purchaseOrderPermissions['change-purchase-order-update-product-quantity-confirmed']"
                          :return-value.sync="item.quantityConfirmed"
                          large
                          dark
                          persistent
                          @save="save(item)"
                          cancel-text="Cancelar"
                          save-text="Guardar">
                          {{ item.quantityConfirmed }}
                          <v-icon light small>mdi-border-color</v-icon>
                          <template v-slot:input>
                            <v-text-field
                              v-model.number="item.quantityConfirmed"
                              label="Edit"
                              type="number"
                              single-line>
                            </v-text-field>
                          </template>
                        </v-edit-dialog>
                        <template v-else>
                          <div class="text-right">
                            <span v-bind:class="{'red--text': item.quantityRequested !== item.quantityConfirmed}">
                              {{ item.quantityConfirmed }}
                            </span>
                          </div>
                        </template>
                      </template>
                      <template v-slot:item.unit.name="{ item }">
                        <div class="d-flex justify-space-between">
                          <div>
                            <div>
                              {{ item.unit.name }}
                            </div>
                            <div v-if="item.unit._id !== item.contentUnit" class="grey--text">
                              de {{ item.contentQuantity }} {{ getUnitName(item.contentUnit) }} c/u
                            </div>
                          </div>
                          <div v-if="['sent'].includes(detail.status) && detail.isRead && purchaseOrderPermissions['change-purchase-order-update-product-quantity-confirmed']">
                            <v-tooltip top>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  icon
                                  color="secondary"
                                  x-small
                                  depressed
                                  @click="openEditVariantInOrder(item)"
                                >
                                  <v-icon small>
                                    mdi-pencil
                                  </v-icon>
                                </v-btn>
                              </template>
                              <span>Cambiar presentación</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </template>
                      <template v-slot:item.ieps="{ item }">
                        ${{ (item.ieps || 0) | toFixNumber(2) }}
                      </template>
                      <template v-slot:item.iva="{ item }">
                        ${{ (item.iva || 0) | toFixNumber(2) }}
                      </template>
                      <template v-slot:item.total="{ item }">
                        ${{ (item.total || 0) | toFixNumber(2) | numberWithComma }}
                      </template>
                      <template v-slot:item.type="{ item }">
                        {{ item.type === 'product' ? 'Producto' : 'Servicio' }}
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-btn
                          x-small
                          fab
                          depressed
                          text
                          class="mr-2"
                          color="error"
                          @click="openRemoveDialog(item)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <template v-slot:footer v-if="['sent'].includes(detail.status) && detail.isRead === true && purchaseOrderPermissions['change-purchase-order-add-product']">
                        <v-btn
                          tile
                          text
                          color="primary"
                          width="100%"
                          @click="openAddProductDialog">
                          <v-icon>
                            mdi-plus
                          </v-icon>
                          {{ $t('orders.buttons.addProduct') }}
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-tab-item>
                  <v-tab-item key="Servicios">
                    <v-data-table
                      :loading-text="servicesTable.title"
                      :loading="servicesTable.loading"
                      :headers="servicesTable.headers"
                      :items="servicesTable.data"
                      :options="servicesTable.options"
                      sort-by="product"
                      class="elevation-1"
                      fixed-header
                      :height="table.data.length >= 10 ? 600 : 400"
                      hide-default-footer>
                      <template v-slot:item.amount="{ item }">
                        ${{ (item.amount || 0) | toFixNumber(2) | numberWithComma }}
                      </template>
                      <template v-slot:footer>
                        <v-btn
                          v-if="detail.status === 'sent' && detail.isRead"
                          tile
                          text
                          color="primary"
                          width="100%"
                          @click="openAddService">
                          <v-icon>
                            mdi-plus
                          </v-icon>
                          Agregar servicio
                        </v-btn>
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-tooltip left>
                          <template
                            v-slot:activator="{ on }">
                            <v-btn
                              fab
                              x-small
                              depressed
                              color="error"
                              v-on="on"
                              @click="remove(item)">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Eliminar servicio</span>
                        </v-tooltip>
                      </template>
                    </v-data-table>
                  </v-tab-item>
                </v-tabs-items>
              </div>

              <div v-else>
                <v-alert dense type="info" v-if="!['received', 'paid'].includes(detail.status) && !detail.payable">
                  <div>
                    Esta orden de compra no se pagará a través de eato.
                  </div>
                  <div>
                    El pago a este proveedor tendrás que hacerlo directamente con él,
                    sin embargo, podrás subir la factura emitida por estos productos para llevar un control de tus finanzas desde eato.
                  </div>
                </v-alert>

                <div class="d-flex justify-end">
                  <div class="font-weight-bold mb-2">
                    Total de artículos: {{ totalProducts }}
                  </div>
                </div>
                <v-data-table
                  :loading-text="table.title"
                  :server-items-length="table.total"
                  :loading="table.loading"
                  :headers="getHeaders"
                  :items="table.data"
                  :options="table.options"
                  sort-by="product"
                  class="elevation-1"
                  fixed-header
                  :height="table.data.length >= 10 ? 600 : 400"
                  hide-default-footer>
                  <template
                    v-slot:item.amount="{ item }"
                    v-if="detail.status === 'sent' && detail.isRead && purchaseOrderPermissions['change-purchase-order-update-product-quantity-confirmed']">
                    <v-edit-dialog
                      :return-value.sync="item.amount"
                      large
                      dark
                      persistent
                      @save="save(item)"
                      cancel-text="Cancelar"
                      save-text="Guardar">
                      ${{ (item.amount || 0) | toFixNumber(2) }}
                      <v-icon light small>mdi-border-color</v-icon>
                      <template v-slot:input>
                        <v-text-field
                          v-model.number="item.amount"
                          label="Edit"
                          single-line>
                        </v-text-field>
                      </template>
                    </v-edit-dialog>
                  </template>
                  <template v-else v-slot:item.amount="{ item }">
                    <div class="text-right">
                      ${{ (item.amount || 0) | toFixNumber(2) | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.unit.name="{ item }">
                    <div>
                      {{ item.unit.name }}
                    </div>
                    <div v-if="item.unit._id !== item.contentUnit" class="grey--text">
                      de {{ item.contentQuantity }} {{ getUnitName(item.contentUnit) }} c/u
                    </div>
                  </template>
                  <template v-slot:item.amountWithTax="{ item }">
                    <div class="text-right">
                      ${{ (item.amountWithTax || 0) | toFixNumber(2) | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.quantityConfirmed="{ item }">
                    <v-edit-dialog
                      v-if="detail.status === 'sent' && detail.isRead"
                      :return-value.sync="item.quantityConfirmed"
                      large
                      dark
                      persistent
                      @save="save(item)"
                      cancel-text="Cancelar"
                      save-text="Guardar">
                      {{ item.quantityConfirmed }}
                      <v-icon light small>mdi-border-color</v-icon>
                      <template v-slot:input>
                        <v-text-field
                          v-model.number="item.quantityConfirmed"
                          label="Edit"
                          type="number"
                          single-line>
                        </v-text-field>
                      </template>
                    </v-edit-dialog>
                    <template v-else>
                      <div class="text-right">
                        <span v-bind:class="{'red--text': item.quantityRequested !== item.quantityConfirmed}">
                          {{ item.quantityConfirmed }}
                        </span>
                      </div>
                    </template>
                  </template>
                  <template v-slot:item.totalWithTax="{ item }">
                    ${{ (item.totalWithTax || 0) | toFixNumber(2) | numberWithComma }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    ${{ (item.total || 0) | toFixNumber(2) | numberWithComma }}
                  </template>
                  <template v-slot:item.type="{ item }">
                    {{ item.type === 'product' ? 'Producto' : 'Servicio' }}
                  </template>
                  <template v-slot:[`item.choice.brand`]="{ item }">
                    {{ item.choice && item.choice.brand ? item.choice.brand : '-' }}
                  </template>
                  <template v-slot:[`item.choice.description`]="{ item }">
                    {{ item.choice && item.choice.description ? item.choice.description : '-' }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      x-small
                      fab
                      depressed
                      text
                      class="mr-2"
                      color="error"
                      @click="openRemoveDialog(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </div>

              <v-row>
                <v-col md="12" class="d-flex flex-wrap flex-column flex-md-row justify-end">
                  <v-btn
                    v-on:click="read"
                    color="success"
                    class="mb-3 ml-md-1"
                    v-if="['sent'].includes(detail.status) && !detail.isRead && purchaseOrderPermissions['change-purchase-order-to-read']">
                    Mandar a confirmar
                  </v-btn>
                  <v-btn
                    v-on:click="onPropagateDialog"
                    color="success"
                    class="mb-3 ml-md-1"
                    v-if="['sent'].includes(detail.status) && detail.isRead && isUserProvider && purchaseOrderPermissions['change-purchase-order-status-to-confirmed']">
                    Confirmar
                  </v-btn>
                  <v-btn
                    v-on:click="pay"
                    color="success"
                    class="mb-3 mr-1" v-if="(['received'].includes(detail.status) && !detail.payable && purchaseOrderPermissions['change-purchase-order-status-to-paid'])">
                    Marcar orden pagada
                  </v-btn>
                  <v-btn
                    v-on:click="attachInvoice(order)"
                    color="success"
                    class="mb-3 mb-md-0 ml-md-1"
                    v-if="['received', 'pending_payment', 'paid'].includes(detail.status)
                      && isValidInvoice
                      && purchaseOrderPermissions['upload-purchase-order-invoice']
                      && detail.provider.receiveWithoutInvoice
                      && countryRestaurant == 'MX'"
                    >
                    Adjuntar factura
                  </v-btn>
                  <v-btn
                    v-on:click="attachInvoice(order)"
                    color="success"
                    class="mb-3 mb-md-0 ml-md-1"
                    v-if="detail.status === 'invalid_invoice'
                      && purchaseOrderPermissions['upload-purchase-order-invoice']
                      && detail.provider.receiveWithoutInvoice
                      && countryRestaurant == 'MX'"
                    >
                    Volver a adjuntar factura
                  </v-btn>
                  <v-btn
                    v-on:click="openCreditNoteDialog"
                    color="success"
                    v-show="show"
                    class="mb-3 mb-md-0 ml-md-1"
                    v-if="['invalid', 'invoiced'].includes(detail.status)
                      && ['provider', 'provider-buyer'].includes($is())
                      && purchaseOrderPermissions['upload-credit-note']"
                    >
                    Subir nota de crédito
                  </v-btn>
                  <v-btn
                    @click.prevent="openGenerateCreditNoteDialog"
                    color="success"
                    v-show="show"
                    class="mb-3 mb-md-0 ml-md-1"
                    v-if="['invalid', 'invoiced'].includes(detail.status)
                      && purchaseOrderPermissions['create-credit-note']
                      && ['provider', 'provider-buyer'].includes($is())"
                    >
                    Generar nota de crédito
                  </v-btn>
                  <v-btn
                    v-on:click="onDeliverDialog"
                    color="success"
                    class="mb-3 mb-md-0 ml-md-1"
                    v-if="!$isMobile && ['invoiced'].includes(detail.status) && isUserProvider && purchaseOrderPermissions['change-purchase-order-status-to-received']">
                    Entregar mercancía
                  </v-btn>
                  <v-btn
                    v-on:click="verifyCSD"
                    color="success"
                    class="mb-3 ml-md-1"
                    v-if="['confirmed', 'invalid'].includes(detail.status) && purchaseOrderPermissions['create-purchase-order-invoice']">
                    Generar factura
                  </v-btn>
                  <v-btn
                    v-on:click="uploadInvoice(order)"
                    color="success"
                    class="mb-3 ml-md-1"
                    v-if="['confirmed', 'invalid'].includes(detail.status)
                      && purchaseOrderPermissions['upload-purchase-order-invoice']
                      && countryRestaurant == 'MX'"
                    >
                    Subir factura
                  </v-btn>
                  <v-btn
                    v-on:click="onCancelOrderDialog"
                    color="error"
                    class="ml-md-1"
                    v-show="show"
                    v-if="isCancelable">
                    Cancelar orden de compra
                  </v-btn>
                  <v-btn
                    v-on:click="onChangeStatus"
                    color="info"
                    class="ml-md-1"
                    v-show="show"
                    v-if="changeStatus">
                    Regresar a un estatus anterior
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-navigation-drawer>

    <v-dialog @keydown.esc="deliverDialog=false" v-model="deliverDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">Recibir mercancía de {{ detail.uuid }}</span>
        </v-card-title>
        <v-card-text>
          <div class="py-3">
            Esta información debe llenarla el encargado de recibir la mercancía.
          </div>
          <div class="py-3">
            <div class="pb-3">
              ¿Estás de acuerdo con la cantidad recibida?
            </div>
            <div class="pb-3">
              <v-btn-toggle
                v-model="agreeDeliver"
                rounded
              >
                <v-btn small color="primary">
                  No
                </v-btn>
                <v-btn small color="primary" data-cy="accept">
                  Sí
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <v-text-field
            data-cy="nip"
            v-if="agreeDeliver === 1"
            dense
            rounded
            outlined
            hide-details
            label="NIP"
            ref="nip"
            placeholder="Introduce tu NIP de seguridad"
            :append-icon="showNip ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showNip ? 'text' : 'password'"
            :rules="rules.required"
            v-model="deliverForm.nip"
            @click:append="showNip = !showNip"
          />
          <div
            v-if="agreeDeliver === 0"
            class="py-3">
            <b>Lo sentimos</b>, pero no es posible recibir la orden de compra ya que nos has indicado que las cantidades son incorrectas.<br>
            No te preocupes, sigue las instrucciones:
            <ol>
              <li>Recibe la mercancia</li>
              <li>Tan pronto te sea posible accede a tu cuenta de Eato</li>
              <li>Abre la orden de compra</li>
              <li>Captura las cantidades que recibiste</li>
              <li>El proveedor generará una nueva factura.</li>
            </ol>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            rounded
            small
            depressed
            @click="onCloseDeliverDialog">
            Cancelar
          </v-btn>
          <v-btn
            data-cy="deliver"
            color="primary"
            rounded
            small
            depressed
            :disabled="!agreeDeliver || agreeDeliver === 0 || !deliverForm.nip"
            @click="deliver">
            Recibir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="propagateDialog=false" v-model="propagateDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <v-btn
            dark
            fab
            x-small
            outlined
            color="primary"
            class="mr-3"
            @click="onClosePropagateDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <span class="headline">Propagar precio</span>
        </v-card-title>
        <v-card-text>
          <div class="py-3">
            Este proceso puede durar varios minutos, por lo que no podras hacer uso de la plataforma hasta finalizar la propagación.
          </div>
          <div class="py-3">
            <div class="pb-3">
              ¿Deseas propagar el precio de los productos modificados a las ordenes de compras?
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            rounded
            small
            depressed
            @click="confirm(false)">
            No
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="confirm(true)">
            Si
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="generateInvoiceDialog=false" v-model="generateInvoiceDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">Generar factura</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            data-cy="form-folio"
            v-model="form.Folio"
            dense
            outlined
            rounded
            hide-selected
            hide-no-data
            label="Folio"
            :rules="rules.required.concat(rules.max)"/>
          <v-autocomplete
            data-cy="form-cfdiUse"
            v-model="form.CfdiUse"
            :items="cfdiUses"
            :filter="searchCfdiUse"
            item-text="name"
            item-value="satId"
            dense
            outlined
            rounded
            hide-selected
            hide-no-data
            :rules="rules.required"
            label="Uso de CFDI">
            <template v-slot:item="{ item }">
              {{ item.satId }} | {{ item.name }}
            </template>
          </v-autocomplete>
          <v-autocomplete
            data-cy="form-paymentForm"
            v-model="form.PaymentForm"
            :items="paymentForms"
            :filter="searchPaymentForm"
            item-text="name"
            item-value="satId"
            dense
            outlined
            rounded
            hide-selected
            hide-no-data
            :rules="rules.required"
            label="Forma de pago">
            <template v-slot:item="{ item }">
              {{ item.satId }} | {{ item.name }}
            </template>
          </v-autocomplete>
          <v-autocomplete
            v-model="form.PaymentMethod"
            :items="paymentMethods"
            item-text="name"
            item-value="satId"
            dense
            outlined
            rounded
            hide-selected
            hide-no-data
            :rules="rules.required"
            label="Método de pago">
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            rounded
            small
            depressed
            @click="onCloseGenerateInvoiceDialog">
            No
          </v-btn>
          <v-btn
            data-cy="generateInvoice"
            color="primary"
            rounded
            small
            depressed
            @click="generateInvoice">
            Generar factura
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="cancelInvoiceDialog=false" v-model="cancelInvoiceDialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span v-if="invoice.source === 'facturapi'" class="headline">¿Quieres CANCELAR la factura {{ invoice.folio }}?</span>
          <span v-if="invoice.source === 'xml'" class="headline">¿Quieres DESVINCULAR la factura {{ invoice.folio }}?</span>
        </v-card-title>
        <v-card-text>
          <v-form v-if="invoice.source === 'facturapi'" ref="motiveForm" v-model="motiveCancellation.valid">
            <v-row>
              <v-col cols="12">
                Al cancelar la factura se invalidará y ya no será válida como comprobante oficial, además se desvinculará de la orden de compra y se cancelará en el SAT. Para continuar seleccione el motivo de cancelación.
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="motiveCancellation.form.motive"
                  rounded
                  outlined
                  dense
                  hide-selected
                  hide-details
                  :items="motiveCancellation.types"
                  :rules="motiveCancellation.rules.required"
                  @change="validateMotiveCancellation"
                  item-text="name"
                  item-value="value"
                  label="Motivo" >
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn icon v-on="on">
                          <v-icon color="dark-grey">mdi-information</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ getTooltipText(motiveCancellation.form.motive) }}</span>
                    </v-tooltip>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" v-if="motiveCancellation.form.motive === '01'">
                <v-text-field
                  v-model="motiveCancellation.form.subtitution"
                  :rules="motiveCancellation.rules.required"
                  rounded
                  outlined
                  dense
                  label="Substitución de factura (UUID factura nueva)"/>
              </v-col>
            </v-row>
          </v-form>
          <span v-if="invoice.source === 'xml'">La factura se invalidará y se desvinculará de la orden de compra.</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            rounded
            small
            depressed
            @click="cancelInvoice">
            <span v-if="invoice.source === 'facturapi'">Sí, cancelar factura</span>
            <span v-if="invoice.source === 'xml'">Sí, desvincular</span>
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="onCloseCancelInvoiceDialog">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="cancelDialog=false" v-model="cancelDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Quieres CANCELAR la orden {{ detail.uuid }}?</span>
        </v-card-title>
        <v-card-text>
          Al cancelar la orden de compra se le notificará al restaurant que su pedido ha sido cancelado y que si desea volver a comprar los productos contigo deberá hacer una nueva orden de compra.
        </v-card-text>
        <v-card-actions>
          <v-radio-group v-model="selectedReason">
            <v-radio value="restaurant_cancelation" label="El restaurante me pidió cancelar"></v-radio>
            <v-radio value="shortage" label="Desabasto"></v-radio>
            <v-radio value="wrong_quantity_or_product" label="Hay un error en el pedido: cantidades y/o producto"></v-radio>
            <v-radio value="restaurant_is_not_a_client" label="El restaurante no es mi cliente"></v-radio>
            <v-radio value="out_delivery_zone_range" label="La dirección del cliente no estña en la zona de entrega"></v-radio>
            <v-radio value="other" label="Otro: "></v-radio>
          </v-radio-group>
        </v-card-actions>
        <v-card-actions v-if="selectedReason === 'other'">
          <v-text-field v-model="otherMotive" label="Especifica el motivo"></v-text-field>
        </v-card-actions>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            data-cy="cancel"
            color="primary"
            rounded
            small
            depressed
            :disabled="!selectedReason || (selectedReason === 'other' && !otherMotive)"
            @click="cancel">
            Sí, cancelar orden
          </v-btn>
          <v-btn
            color="error"
            rounded
            small
            depressed
            @click="onCloseCancelOrderDialog">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="refundDialog=false" v-model="refundDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Quieres SOLICITAR DEVOLUCIÓN de la orden {{ detail.uuid }}?</span>
        </v-card-title>
        <v-card-text>
          <div>
            Al solicitar una devolución tu orden será cancelada y se eliminará la factura para que la vuelvas a subir.
          </div>
          <div>
            <v-checkbox
              v-model="forceCancelInvoice"
              label="Quiero que la factura se cancele"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            rounded
            small
            depressed
            @click="refund">
            Sí, devolver saldo
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="onCloseRefundOrderDialog">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="removeDialog=false" v-model="removeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Borrar {{ itemToRemove.name }}?</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            outlined
            rounded
            small
            depressed
            @click="onCloseRemoveDialog">
            No
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="removeProduct">
            Sí
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="creditNoteDialog=false" v-model="creditNoteDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <div class="d-flex">
            <div class="primary--text">
              Nota de crédito
            </div>
            <v-spacer />
            <v-btn
              color="black darken-1"
              @click="onCloseCreditNoteDialog"
              text
              icon>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <v-form v-model="form.isValid" ref="form">
            <v-file-input
              v-model="form.xmlFiles"
              multiple
              label="XML Files"
              accept=".xml"
              required>
            </v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="uploadCreditNote">
            Subir nota de crédito
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog @keydown.esc="changeStatusDialog=false" v-model="changeStatusDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">¿Quieres cambiar de estatus la orden {{ detail.uuid }}?</span>
        </v-card-title>
        <v-card-text style="text-align: justify">
          Al cambiar de estatus la orden de compra será regresada a su estatus anterior. De está forma podrás hacer los cambios respectivos.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            data-cy="cancel"
            color="primary"
            text
            rounded
            small
            depressed
            @click="changeStatusOrder">
            Sí, cambiar de estatus
          </v-btn>
          <v-btn
            color="error"
            rounded
            small
            depressed
            @click="onCloseChangeStatus">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <bill-dialog
      :dialog="billDialog"
      :order="order"
      @close="closeBillDialog">
    </bill-dialog>

    <attach-dialog
      :dialog="attachDialog"
      :order="order"
      @close="closeAttachDialog">
    </attach-dialog>

    <v-dialog
      @keydown.esc="servicesDialog.open=false"
      v-model="servicesDialog.open"
      max-width="400"
      persistent>
      <v-card>
        <v-card-title>
          ¿Cuál servicio agregarías?
        </v-card-title>
        <v-card-text>
          <v-form v-model="servicesDialog.valid" ref="serviceForm">
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="servicesDialog.form.service"
                  :items="servicesDialog.services"
                  :loading="servicesDialog.loading"
                  label="Servicio"
                  item-text="product.name"
                  item-value="id"
                  clearable
                  hide-selected
                  hide-no-data
                  return-object
                  outlined
                  rounded
                  dense
                  hide-details
                  :rules="servicesDialog.rules.service"
                  @change="validateService(servicesDialog.form.service)"
                  @reset="servicesDialog.form.amount = 0"
                  :search-input.sync="searchService" />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.number="servicesDialog.form.amount"
                  label="Precio"
                  outlined
                  rounded
                  dense
                  hide-details
                  prefix="$"
                  :rules="servicesDialog.rules.amount"
                  type="number" />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            outlined
            rounded
            small
            depressed
            @click="onCloseAddServiceDialog">
            Ninguno
          </v-btn>
          <v-btn
            rounded
            color="primary"
            @click="addService">
            Agregar servicio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="thanksDialog=false"
      v-model="thanksDialog"
      max-width="400">
      <v-card>
        <v-card-title>
          ¡Gracias!
        </v-card-title>
        <v-card-text>
          Pronto publicaremos los nuevos productos para que más restaurantes puedan usarlos.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            rounded
            color="primary"
            @click.stop="thanksDialog = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="showProductsToInventory=false"
      v-model="showProductsToInventory"
      :max-width="productsToInventory.data.length ? 600 : 450">
      <v-card>
        <v-card-title>
          Productos a inventariar
        </v-card-title>
        <v-card-text>
          <div v-if="productsToInventory.data.length">
           <v-data-table
              :loading-text="productsToInventory.title"
              :loading="productsToInventory.loading"
              :headers="productsToInventory.headers"
              :items="productsToInventory.data"
              :options="productsToInventory.options"
              sort-by="product"
              class="elevation-1"
              fixed-header
              hide-default-footer>
            </v-data-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="showProductsToInventory = false">
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddProductToOrder
      v-if="$is() === 'provider'"
      ref="addProductToOrder"
      @updated="load()"
    />

    <SendOrderByEmail
      ref="sendOrderByEmail"
      :order="order._id"
    />

    <AddCSD
      :isOpenDialog="onAddCSD"
      @close="closeOnAddCSD"
    />

    <GenerateCreditNote
      :isOpenDialog.sync="creditNoteOpen"
      :invoices="detail.invoices"
      :products="table.data"
      :order="detail"
      @close="addCreditNoteInOrder"/>

    <CreditNoteViewer
      :isOpenDialog.sync="creditNoteViewerOpen"
      :order="detail"
      :file="file"
      :key="`creditNote-${detail.id}`"
      @close="creditNoteViewerOpen = false"/>

    <InvoiceViewer
      ref="invoice"
      :order="detail"
      :key="`invoice-${detail.id}`"
      @close="invoiceViewerOpen = false"/>

    <EditVariantInOrder
      v-if="$is() === 'provider'"
      ref="editVariantInOrder"
      @updated="load()"
    />

    <CheckStatusInvoice
      ref="checkStatusInvoice"
    />

    <PropagatePriceToOrders ref="propagatePriceToOrders" />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-overlay :value="overlayPropagate" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import CheckStatusInvoice from '@/components/modals/CheckStatusInvoice'
import AddProductToOrder from '@/components/modals/AddProductToOrder'
import AddCSD from '@/components/modals/AddCSD'
import InvoiceViewer from '@/components/modals/InvoiceViewer'
import CreditNoteViewer from '@/components/modals/CreditNoteViewer'
import billDialog from '@/screens/purchaseOrders/Bill'
import attachDialog from '@/components/modals/AttachInvoice'
import Order from '@/models/Order'
import SendOrderByEmail from '@/components/modals/SendOrderByEmail'
import { cfdiTypes, cfdiUses, paymentForms, paymentMethods } from '@/utils/constants'
import debounce from 'lodash/debounce'
import print from 'print-js'
import { mapGetters } from 'vuex'
import EditVariantInOrder from '@/components/modals/EditVariantInOrder'
import GenerateCreditNote from '@/components/modals/CreateCreditNote'
import PropagatePriceToOrders from '@/components/modals/PropagatePriceToOrders'

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: () => ({})
    },
    show: {
      type: Boolean,
      default: true
    }
  },
  components: {
    AddProductToOrder,
    billDialog,
    InvoiceViewer,
    CreditNoteViewer,
    AddCSD,
    SendOrderByEmail,
    attachDialog,
    EditVariantInOrder,
    GenerateCreditNote,
    PropagatePriceToOrders,
    CheckStatusInvoice
  },
  data () {
    let orderModel = new Order()

    return {
      showProductsToInventory: false,
      invoiceViewerOpen: false,
      creditNoteViewerOpen: false,
      creditNoteOpen: false,
      onAddCSD: false,
      panel: {
        activated: false
      },
      tab: 0,
      orderModel,
      overlay: false,
      disablee: false,
      cancelInvoiceDialog: false,
      generateInvoiceDialog: false,
      addProductDialog: false,
      billDialog: false,
      cancelDialog: false,
      refundDialog: false,
      removeDialog: false,
      thanksDialog: false,
      attachDialog: false,
      changeStatusDialog: false,
      creditNoteDialog: false,
      forceCancelInvoice: false,
      detail: {},
      table: {
        title: 'Loading products',
        loading: false,
        data: [],
        total: 0,
        headers: this.getHeaders,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      tableOriginalProducts: {
        title: 'Loading products',
        loading: false,
        data: [],
        total: 0,
        headers: this.getHeaders,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      items: [
        { title: 'Visualizador de productos originales' }
      ],
      productsToInventory: {
        title: 'Loading products',
        loading: false,
        data: [],
        headers: [
          { text: 'Producto', value: 'product', sortable: false },
          { text: 'Cantidad a Invetariar', value: 'quantity', sortable: false },
          { text: 'Unidad de Medida', value: 'unit', sortable: false }
        ],
        total: 0,
        options: {
          itemsPerPage: 10,
          page: 1
        }
      },
      servicesTable: {
        title: 'Loading services',
        loading: false,
        data: [],
        headers: [
          { text: 'Clave SAT', value: 'satCode' },
          { text: 'Nombre', value: 'product.name' },
          { text: 'Precio', value: 'amount' },
          { text: 'Acciones', value: 'actions' }
        ],
        total: 0,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      rules: {
        required: [v => !!v || this.$t('validations.required')],
        max: [v => v.length <= 9 || this.$t('validations.max', { count: 9 })]
      },
      file: '',
      itemToRemove: {},
      cfdiUses,
      paymentForms,
      cfdiTypes,
      paymentMethods,
      form: {
        Folio: '',
        CfdiUse: cfdiUses[0].satId,
        PaymentForm: paymentForms[21].satId,
        PaymentMethod: paymentMethods[1].satId,
        xmlFiles: []
      },
      invoice: {},
      invoiceStatuses: {
        active: {
          text: 'Activa',
          color: 'primary'
        },
        canceled: {
          text: 'Cancelada',
          color: 'red'
        }
      },
      invoiceSource: {
        facturapi: 'EATO',
        xml: 'Externo'
      },
      servicesDialog: {
        open: false,
        loading: false,
        services: [],
        valid: false,
        form: {
          service: null,
          amount: 0,
          quantity: 1
        },
        rules: {
          satCode: [v => !!v || this.$t('validations.satCode')],
          service: [v => !!v || this.$t('validations.service')],
          amount: [v => !!v || this.$t('validations.amount')]
        }
      },
      searchService: '',
      deliverDialog: false,
      propagateDialog: false,
      deliverForm: {
        nip: null
      },
      agreeDeliver: null,
      showNip: false,
      notes: '',
      isModificated: false,
      selectedArea: {},
      motiveCancellation: {
        valid: false,
        types: [
          { value: '01', name: 'Comprobante emitido con errores con relación' },
          { value: '02', name: 'Comprobante emitido con errores sin relación.' },
          { value: '03', name: 'No se llevó a cabo la operación' },
          { value: '04', name: 'Operación nominativa relacionada en la factura global.' }
        ],
        form: {
          motive: null,
          subtitution: null
        },
        rules: {
          required: [v => !!v || this.$t('validations.required')]
        }
      },
      selectedReason: '',
      otherMotive: ''
    }
  },
  watch: {
    order: async function (value) {
      if (value && (value._id || (value.$ && value.$.id))) {
        this.orderModel = new Order(value.$ || value)
        await this.load()
      }
    },
    dialog: function (val) {
      this.panel.activated = val
    },
    searchService: debounce(async function (val) {
      const params = {
        name: val,
        type: 'service',
        sort: 'product'
      }
      const response = await this.$api.get(`/v1/catalog`, params)
      this.servicesDialog.services = response.data
    }, 300)
  },
  computed: {
    actualDate () {
      const nowDate = new Date()
      if (nowDate.getDay() === 4) return true

      return false
    },
    ...mapGetters({
      countryRestaurant: 'currentRestaurant/country'
    }),
    downloadOrderInvoice () {
      let url = `${process.env.VUE_APP_API_URL}/v1/orders/${this.detail._id}/download-invoice-pdf`
      url += `?access_token=${this.$store.state.login.token}`
      return url
    },
    overlayPropagate () {
      return this.$store.state.currentProvider.loadingPropagate
    },
    totalProducts () {
      return this.table.data.length
    },
    getHeaders () {
      const _headers = [
        { text: '#', value: 'number', sortable: false },
        { text: 'Insumo', value: 'name', sortable: false },
        { text: 'Tipo', value: 'type', sortable: false },
        { text: 'Marca', value: 'choice.brand', sortable: false },
        { text: 'Descripción', value: 'choice.description', sortable: false }
      ]

      if (!['sent'].includes(this.detail.status) || !this.detail.isRead) {
        _headers.push(
          { text: 'Cantidad solicitada', value: 'quantityRequested', sortable: false, align: 'end' },
          { text: 'Unidad de medida', value: 'unit.name', sortable: false }
        )
      }

      if (['sent', 'confirmed'].includes(this.detail.status)) {
        _headers.push({ text: 'Cantidad confirmada', value: 'quantityConfirmed', sortable: false })

        _headers.push(
          { text: 'Unidad de medida', value: 'unit.name', sortable: false },
          { text: 'Precio unitario (Sin impuestos)', value: 'amount', sortable: false }
        )
        if (this.hasTaxes(this.table.data)) {
          _headers.push({ text: 'IVA', value: 'iva', sortable: false })
        }
        if (this.hasTaxes(this.table.data)) {
          _headers.push({ text: 'IEPS', value: 'ieps', sortable: false })
        }
      } else {
        _headers.push({ text: 'Precio unitario (Con impuestos)', value: 'amountWithTax', sortable: false, align: 'end' })
      }

      if (['invoiced', 'received', 'invalid'].includes(this.detail.status)) {
        _headers.push({ text: 'Cantidad confirmada', value: 'quantityConfirmed', sortable: false, align: 'end' })
      }

      if (['received', 'invalid'].includes(this.detail.status)) {
        _headers.push({ text: 'Cantidad entregada', value: 'quantityReceived', sortable: false, align: 'end' })
      }

      if (['sent', 'confirmed'].includes(this.detail.status)) {
        _headers.push({ text: 'Total', value: 'total', sortable: false, align: 'end' })
      } else {
        _headers.push({ text: 'Total', value: 'totalWithTax', sortable: false, align: 'end' })
      }

      return _headers
    },
    total () {
      return this.detail.total
    },
    totalToPay () {
      let total = this.detail.total

      if (['sent', 'confirmed'].includes(this.detail.status) && this.$is() === 'provider') {
        total = this.table.data.length
          ? this.table.data.reduce((acc, num) => {
            acc = acc + num.total
            return acc
          }, 0)
          : 0
      }

      return total
    },
    subtotal () {
      if (['invoiced', 'received', 'pending_payment', 'paid'].includes(this.detail.status)) {
        return this.table.data.length ? this.table.data.reduce((acc, num) => {
          acc = acc + (num.amount * num.quantityReceived)
          return acc
        }, 0) : 0
      } else {
        return this.table.data.length ? this.table.data.reduce((acc, num) => {
          acc = acc + (num.amount * num.quantityConfirmed)
          return acc
        }, 0) : 0
      }
    },
    isr () {
      return this.detail.invoices.length ? this.detail.invoices.reduce((acc, b) => {
        return acc + b.ISR
      }, 0) : 0
    },
    iva () {
      return this.detail.invoices.length ? this.detail.invoices.reduce((acc, b) => {
        return acc + b.IVA
      }, 0) : 0
    },
    ieps () {
      return this.detail.invoices.length ? this.detail.invoices.reduce((acc, b) => {
        return acc + b.IEPS
      }, 0) : 0
    },
    IVAFromProducts () {
      return this.table.data.length ? this.table.data.reduce((acc, product) => {
        const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
        acc = acc + (iva ? (iva.base / 100) * (product.amount + this.ieps) * product.quantityConfirmed : 0)
        return acc
      }, 0) : 0
    },
    IEPSFromProducts () {
      return this.table.data.length ? this.table.data.reduce((acc, product) => {
        const iepsTaxes = product.taxes.filter(tax => tax.satCode === '003' && tax.base > 0)
        const hasBaseAmount = (iepsTaxes || []).every(tax => tax.baseAmount && tax.baseAmount >= 0)
        if (hasBaseAmount) {
          const taxesAmount = iepsTaxes.reduce((acc, tax) => {
            const base = tax.isQuota ? tax.base : (tax.base / 100)
            return acc + (tax.baseAmount * base)
          }, 0)
          return taxesAmount
        }
        const ieps = product.taxes.find(tax => tax.satCode === '003' && tax.base > 0)
        const amount = product.amount - (product.discount || 0)
        acc = acc + (ieps ? (ieps.base / 100) * amount * product.quantityConfirmed : 0)
        return acc
      }, 0) : 0
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    isCancelable () {
      return !['received', 'paid', 'canceled', 'pending_payment', 'invalid_invoice'].includes(this.detail.status) && this.purchaseOrderPermissions['change-purchase-order-status-to-cancelled']
    },
    changeStatus () {
      return !['received', 'paid', 'canceled', 'pending_payment', 'invalid_invoice', 'invoiced', 'invalid'].includes(this.detail.status) && this.detail.isRead
    },
    provider () {
      return this.$store.state.currentProvider.data || ''
    },
    copyToLink () {
      return `${process.env.VUE_APP_API_URL}/v1/orders/${this.orderModel.$._id}/download`
    },
    purchaseOrderPermissions () {
      return this.$store.state.login.permissions['purchase-orders'].components['purchase-orders-list'].actions
    },
    isUserProvider () {
      return this.$store.state.userProvider
    },
    isValidInvoice () {
      const totalInvoice = this.detail.invoices.length ? this.detail.invoices.reduce((acc, invoice) => {
        acc = acc + invoice.total
        return acc
      }, 0) : 0
      return this.detail.total.toFixed(2) !== totalInvoice.toFixed(2)
    },
    quantityOriginalProducts () {
      return this.tableOriginalProducts.data.length
    },
    totalOriginalProducts () {
      return this.tableOriginalProducts.data.length ? this.tableOriginalProducts.data.reduce((acc, num) => {
        acc = acc + num.totalWithTax
        return acc
      }, 0) : 0
    },
    totalInvoices () {
      if (!this.detail.invoices) return 0
      return this.detail.invoices.reduce((acc, curr) => acc + curr.total, 0)
    }
  },
  mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  methods: {
    async downloadInvoice (item) {
      try {
        const response = await this.$api.get(`/v1/orders/${this.detail._id}/invoices/${item._id}`)
        var element = document.createElement('a')
        element.setAttribute('href', 'data:text/xml;charset=utf-8,' + encodeURIComponent(response.content))
        element.setAttribute('download', `invoice-${item.uuid}`)

        element.style.display = 'none'
        document.body.appendChild(element)

        element.click()

        document.body.removeChild(element)
      } catch (error) {
        this.$toasted.show('Error al descargar la factura', { duration: 3500, type: 'error', position: 'top-center', iconPack: 'mdi', icon: 'mdi-cancel' })
      }
    },
    async checkStatusInvoice (item) {
      this.overlay = true
      try {
        const { response } = await this.orderModel.checkStatus(item._id)
        this.openCheckStatusInvoice(item, response.data)
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3500, type: 'error', position: 'top-center', iconPack: 'mdi', icon: 'mdi-cancel' })
      }
      this.overlay = false
    },
    openCheckStatusInvoice (invoice, data) {
      this.$refs.checkStatusInvoice.open(invoice, data)
    },
    keyDownHandler (event) {
      if (event.code === 'Escape') {
        this.isModificated = false
        this.$emit('close')
      }
    },
    async updatePrice (item) {
      this.overlay = true
      try {
        const { response } = await this.orderModel.refreshProducts(item._id)
        this.detail = response.data
        this.table.data = this.getProductsPricesWithTaxes(response.data.products)
        this.$emit('update', new Order(this.detail))
        this.$toasted.show(`El precio del producto ${item.name} se actualizo correctamente`, { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    onCopy: function (e) {
      this.$toasted.show('Link copiado', { duration: 3000 })
    },
    async closeOnAddCSD () {
      this.onAddCSD = false
      try {
        const response = await this.$api.get(`/v1/providers/${this.provider._id}/settings`)
        if (response.taxInformation.csdId) {
          this.generateInvoiceDialog = true
        }
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    getUnitName (unitId) {
      const unit = this.$store.state.login.units.data.find(unit => unit.id === unitId)
      return unit ? unit.name : ''
    },
    openAddProductDialog () {
      this.$refs.addProductToOrder.open(this.detail)
    },
    openSendByEmailDialog () {
      this.$refs.sendOrderByEmail.openDialog()
    },
    findIEPS (taxes) {
      if (taxes.length) {
        const ieps = taxes.find(tax => tax.satCode === '003' && tax.base > 0)
        return ieps
      }
      return null
    },
    hasTaxes (products) {
      return products.some(product => product.taxes.length)
    },
    resetServiceDialog () {
      this.servicesDialog.open = false
      this.servicesDialog.loading = false
      this.servicesDialog.valid = false
      this.servicesDialog.services = []
      this.servicesDialog.serviceSelected = null
      this.servicesDialog.form = {
        valid: false,
        satCode: '',
        name: '',
        amount: 0
      }
      this.searchService = ''
      this.$refs.serviceForm.resetValidation()
    },
    openAddService: function () {
      this.servicesDialog.open = true
    },
    onCloseAddServiceDialog: function () {
      this.servicesDialog.open = false
      this.resetServiceDialog()
    },
    addService: function () {
      const payload = this.servicesDialog.form.service
      payload.amount = this.servicesDialog.form.amount
      payload.quantity = this.servicesDialog.form.quantity
      const valid = this.$refs.serviceForm.validate()
      if (valid) {
        this.servicesTable.data.push(payload)
        this.servicesTable.total = this.servicesTable.data.length
        this.onCloseAddServiceDialog()
      }
    },
    remove (item) {
      const index = this.servicesTable.data.findIndex(i => i.id === item.id)
      this.servicesTable.data.splice(index, 1)
      this.servicesTable.total = this.servicesTable.data.length
    },
    cancelInvoice: async function () {
      if ((this.invoice.source === 'facturapi' && this.$refs.motiveForm.validate()) || (this.invoice.source === 'xml')) {
        this.overlay = true
        try {
          await this.$api.post(`/v1/orders/${this.detail._id}/invoices/${this.invoice._id}/cancel`, this.motiveCancellation.form)
          this.onCloseCancelInvoiceDialog()
          if (this.order.id) {
            await this.load({
              limit: this.table.options.itemsPerPage,
              skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
            })
            this.$toasted.show('La factura se ha cancelado correctamente.', { duration: 3000 })
          }
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    openCancelInvoiceDialog (invoice) {
      this.invoice = invoice
      this.cancelInvoiceDialog = true
    },
    onCloseCancelInvoiceDialog () {
      this.motiveCancellation.valid = false
      this.motiveCancellation.form = {
        motive: null
      }
      this.invoice = {}
      this.cancelInvoiceDialog = false
    },
    onCloseGenerateInvoiceDialog () {
      this.resetGenerateInvoiceForm()
      this.generateInvoiceDialog = false
    },
    openCreditNoteDialog () {
      this.creditNoteDialog = true
    },
    onCloseCreditNoteDialog () {
      this.resetGenerateInvoiceForm()
      this.creditNoteDialog = false
    },
    uploadCreditNote: async function () {
      try {
        this.overlay = true
        await this.$api.post(`/v1/orders/${this.order._id}/credit-notes`, this.form, { delivery: 'formData' })
        await this.load({
          limit: this.table.options.itemsPerPage,
          skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
        })
        this.$emit('update', new Order(this.detail))
        this.onCloseCreditNoteDialog()
        this.$toasted.show('La nota de crédito se carga correctamente.', { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    checkLastFolio: async function () {
      try {
        const response = await this.$api.post(`/v1/invoices/${this.$store.state.currentProvider.taxInformation.data.taxInformation.taxId}/folio`)
        this.form.Folio = response.folio
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    generateInvoice: async function () {
      this.overlay = true
      try {
        let data = {}
        let prices = []
        data = this.form
        for (const product of this.table.data) {
          prices.push({ amount: product.amount, id: product.choice.id, product: product.product, contentUnit: product.contentUnit })
        }
        data.prices = prices
        await this.$api.post(`/v1/orders/${this.detail._id}/invoices`, data)
        this.onCloseGenerateInvoiceDialog()
        if (this.order.id) {
          await this.load({
            limit: this.table.options.itemsPerPage,
            skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
          })
        }
        this.$emit('update', new Order(this.detail))
        this.$toasted.show('La factura se creó exitosamente.', { duration: 3000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    resetGenerateInvoiceForm () {
      this.form = {
        Folio: '',
        CfdiUse: cfdiUses[0].satId,
        PaymentForm: paymentForms[21].satId,
        PaymentMethod: paymentMethods[1].satId,
        InvoiceType: '',
        xmlFiles: []
      }
    },
    searchCfdiUse (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    searchPaymentForm (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    searchCfdiType (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    async verifyCSD () {
      if (typeof this.detail.provider.taxInformation.organizationId !== 'undefined') {
        try {
          const response = await this.$api.get(`/v1/providers/${this.detail.provider._id}/settings/${this.detail.provider.taxInformation.organizationId}/organization`)

          if (response.certificate.has_certificate) {
            this.checkLastFolio()
            this.generateInvoiceDialog = true
          } else {
            this.$toasted.show('Carge nuevamente los certificados para generar la factura', { duration: 3000 })
          }
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      } else {
        this.$toasted.show('Carge nuevamente los certificados para generar la factura', { duration: 3000 })
      }
    },
    onGenerateInvoiceDialog () {
      this.generateInvoiceDialog = true
    },
    viewInvoice: async function (invoice) {
      this.$refs.invoice.open(invoice)
    },
    viewCreditNote: async function (file) {
      if (file.xml) {
        this.$refs.invoice.open(file)
      } else {
        this.file = file._id
        this.creditNoteViewerOpen = true
      }
    },
    statusNames (status) {
      return {
        created: 'Creada',
        sent: 'Nueva orden',
        confirmed: 'Confirmada',
        canceled: 'Cancelada',
        invoiced: 'Facturada',
        invalid: 'Factura no coincide',
        received: 'Entregada',
        pending_payment: 'Pendiente de pago',
        paid: 'Cobrada'
      }[status]
    },
    statusColors (status) {
      return {
        created: 'grey',
        sent: 'grey',
        confirmed: 'info',
        canceled: 'red',
        invoiced: 'info',
        invalid: 'red',
        invalid_invoice: 'red',
        received: 'primary',
        pending_payment: 'primary',
        paid: 'primary'
      }[status]
    },
    save: function (item = {}) {
      const product = this.table.data.find(p => p._id === item._id)
      const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
      const ieps = product.taxes.find(tax => tax.satCode === '003' && tax.base > 0)
      const amount = product.amount - (product.discount || 0)
      const productNotModificated = this.detail.products.find(x => x._id === item._id)
      const iepsAmount = ieps ? (ieps.isQuota ? ieps.base : ieps.base / 100) * amount : 0
      if (product.taxes.length === 0 || !product.taxes) {
        product.taxes = []
      } else {
        product.taxes[0].baseAmount = item.amount
      }
      product.amount = item.amount
      product.quantityConfirmed = item.quantityConfirmed
      item.iva = iva ? ((iva.base / 100) * item.amount) + iepsAmount : 0
      item.ieps = iepsAmount
      item.total = item.amount * item.quantityConfirmed

      this.calculateTotalProduct()
      this.saveChanges()
      // this.validate()

      if (productNotModificated.amount !== item.amount) {
        this.$refs.propagatePriceToOrders.open(item, this.detail._id)
      }
    },
    async validate () {
      this.overlay = true

      const payload = {
        restaurant: this.detail.restaurant._id || this.detail.restaurant,
        items: this.table.data.map(prod => {
          return {
            id: prod._id,
            quantity: prod.quantityConfirmed,
            amount: prod.amount,
            taxes: prod.taxes
          }
        })
      }

      try {
        await this.$api.post(`/v1/orders/${this.detail.uuid}/validate-purchase/${this.detail.area._id}`, payload)
        this.saveChanges()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
        await this.load()
      }
      this.overlay = false
    },
    calculateTotalProduct () {
      let total = this.detail.total
      if (['sent', 'confirmed'].includes(this.detail.status)) {
        total = this.table.data.reduce((acc, product) => {
          const productTaxes = product.taxes || []
          const productWithCalculatedTaxes = this.calculatePriceProductWithTaxes(product.amount, productTaxes)
          product.total = productWithCalculatedTaxes * product.quantityConfirmed
          acc += product.total
          return acc
        }, 0)
      }
      if (this.detail.status === 'invoiced') {
        total = this.table.data.reduce((acc, product) => {
          const productTaxes = product.taxes || []
          product.total = this.calculatePriceProductWithTaxes(product, productTaxes) * product.quantityReceived
          acc += product.total
          return acc
        }, 0)
      }
      this.detail.total = total
    },
    calculateCommission (total) {
      const feeRate = (this.restaurant.fees.purchaseOrder / 100) * 1.16
      return total * feeRate
    },
    closeDialog () {
      this.isModificated = false
      this.$emit('close')
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      if (this.order.id) {
        await this.load({
          limit: data.itemsPerPage,
          skip: (data.itemsPerPage * (data.page - 1)),
          sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`
        })
      }
    },
    async load (params = null) {
      this.overlay = true
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
      }
      try {
        const { response } = await this.orderModel.detail({ params })
        this.table.loading = false
        response.data.products.map((element, index) => {
          element.number = index + 1
        })
        this.detail = response.data
        this.table.data = this.getProductsPricesWithTaxes(response.data.products)
        this.table.total = response.data.products.length
        if (response.data.originalProducts) {
          response.data.originalProducts.map((element, index) => { element.number = index + 1 })
          this.tableOriginalProducts.data = this.getProductsPricesWithTaxes(response.data.originalProducts)
          this.tableOriginalProducts.total = response.data.products.length
        }
        this.$emit('update', new Order(this.detail))
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
      this.table.loading = false
    },
    getProductsPricesWithTaxes (products) {
      return ['provider'].includes(this.$is()) && ['sent', 'confirmed'].includes(this.detail.status)
        ? products.map(product => {
          const priceWithTaxes = this.calculatePriceProductWithTaxes(product.amount, product.taxes)
          const unitAmountWithTaxes = priceWithTaxes
          const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
          const ieps = this.findIEPS(product.taxes)
          const iepsAmount = ieps ? (ieps.isQuota ? ieps.base : ieps.base / 100) * (product.amount - (product.discount || 0)) : 0
          return Object.assign(
            {},
            product,
            {
              ieps: iepsAmount,
              iva: iva ? (iva.base / 100) * (product.amount + iepsAmount) : 0,
              unitAmountWithTaxes,
              total: unitAmountWithTaxes * product.quantityConfirmed
            }
          )
        })
        : products
    },
    calculatePriceProductWithTaxes (amount, taxes = []) {
      const hasBaseAmount = (taxes || []).every(tax => tax.baseAmount && tax.baseAmount >= 0)
      if (hasBaseAmount && taxes.length > 0) {
        const taxesAmount = taxes.reduce((acc, tax) => {
          const taxBase = tax.isQuota ? tax.base : (tax.base / 100)
          if (tax.isRetention) {
            return acc - (tax.baseAmount * taxBase)
          }
          return acc + (tax.baseAmount * taxBase)
        }, 0)
        return amount + taxesAmount
      } else {
        const iva = taxes.find(tax => tax.satCode === '002' && tax.base > 0)
        const taxesInQuota = taxes.filter(tax => tax.isQuota && tax.satCode !== '002')
        const taxesNoQuota = taxes.filter(tax => !tax.isQuota && tax.satCode !== '002')

        const amountTaxInQuota = taxesInQuota.map(tax => {
          return tax.base
        })

        const amountTaxNoQuota = taxesNoQuota.map(tax => {
          return (tax.base / 100) * amount
        })

        amount = amountTaxInQuota.reduce((acc, tax) => {
          return acc + tax
        }, amount)

        amount = amountTaxNoQuota.reduce((acc, tax) => {
          return acc + tax
        }, amount)

        if (iva) {
          amount += (iva.base / 100) * amount
        }

        return amount
      }
    },
    convertToDateString (date) {
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    cancel: async function (data) {
      this.overlay = true
      if (this.selectedReason === 'other') this.selectedReason = this.otherMotive
      const payload = {
        restaurant: this.detail.restaurant._id || this.detail.restaurant,
        cancelationMotive: this.selectedReason
      }
      try {
        const { response } = await this.orderModel.cancel(payload)
        this.detail = response.data
        this.cancelDialog = false
        this.$emit('update', new Order(this.detail))
        this.selectedReason = ''
        this.otherMotive = ''
        this.closeDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    refund: async function (data) {
      this.overlay = true
      const payload = {
        restaurant: this.detail.restaurant,
        cancelInvoice: this.forceCancelInvoice
      }
      try {
        const { response } = await this.orderModel.refund(payload)
        this.detail = response.data
        this.detail.status = 'canceled'
        this.refundDialog = false
        this.forceCancelInvoice = false
        this.$emit('update', new Order(this.detail))
        this.closeDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    pay: async function () {
      this.overlay = true
      try {
        const { response } = await this.orderModel.pay()
        this.detail = response.data
        this.$emit('update', new Order(this.detail))
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    read: async function () {
      this.overlay = true
      try {
        const { response } = await this.orderModel.update({ isRead: true, restaurant: this.detail.restaurant._id })
        this.detail = response.data
        this.$emit('update', new Order(this.detail))
        this.closeDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    saveChanges: async function () {
      this.overlay = true

      const payload = {
        restaurant: this.detail.restaurant._id || this.detail.restaurant,
        items: this.table.data.map(prod => {
          return {
            id: prod._id,
            quantity: prod.quantityConfirmed,
            amount: prod.amount,
            taxes: prod.taxes
          }
        })
      }

      try {
        const { response } = await this.orderModel.update(payload)
        this.detail = response.data
        this.$emit('update', new Order(this.detail))
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    confirm: async function (propagate) {
      this.overlay = true

      const payload = {
        restaurant: this.detail.restaurant._id,
        items: this.table.data.map(prod => {
          return {
            id: prod._id,
            quantity: prod.quantityConfirmed,
            amount: prod.amount,
            taxes: prod.taxes,
            product: prod.product._id
          }
        })
      }
      const services = this.servicesTable.data.map(serv => {
        return { id: serv.product.id || serv._id, quantity: 1, amount: serv.amount, product: serv.product._id }
      })
      let prices = []
      for (const product of this.table.data) {
        prices.push({ amount: product.amount, id: product.choice, product: product.product, contentUnit: product.contentUnit })
      }
      payload.propagate = propagate
      payload.prices = prices
      payload.items = payload.items.concat(services)
      try {
        const { response } = await this.orderModel.confirm(payload)
        this.detail = response.data
        this.$emit('update', new Order(this.detail))
        this.isModificated = false
        this.onClosePropagateDialog()
        this.closeDialog()
      } catch (error) {
        this.$toasted.show(
          error.response.response.data.message,
          {
            action: {
              text: 'Cerrar',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
              }
            }
          }
        )
      }
      this.overlay = false
    },
    onRefundOrderDialog: function () {
      this.refundDialog = true
    },
    onCloseRefundOrderDialog: function () {
      this.forceCancelInvoice = false
      this.refundDialog = false
    },
    onCancelOrderDialog: function () {
      this.cancelDialog = true
    },
    onCloseCancelOrderDialog: function () {
      this.selectedReason = ''
      this.otherMotive = ''
      this.cancelDialog = false
    },
    uploadInvoice: async function (order) {
      this.billDialog = true
    },
    closeBillDialog: async function () {
      await this.load()
      this.billDialog = false
      this.$emit('update', new Order(this.detail))
    },
    openRemoveDialog (item) {
      this.removeDialog = true
      this.itemToRemove = item
    },
    onCloseRemoveDialog () {
      this.removeDialog = false
      this.itemToRemove = {}
    },
    async removeProduct () {
      this.overlay = true
      const payload = {
        restaurant: this.detail.restaurant,
        items: this.table.data
          .filter(prod => prod.id !== this.itemToRemove.id)
          .map(prod => {
            return { id: prod._id, quantity: prod.quantityRequested, amount: prod.amount }
          })
      }
      try {
        const { response } = await this.orderModel.update(payload)
        this.detail = response.data
        if (response.data.products.length > 0) {
          this.$emit('update', new Order(this.detail))
        } else {
          this.$emit('remove', new Order(this.detail))
        }

        this.closeDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.onCloseRemoveDialog()
      this.overlay = false
    },
    resetDeliverDialog () {
      this.deliverForm = {
        nip: null
      }
      this.agreeDeliver = null
      this.$refs.nip.resetValidation()
    },
    onDeliverDialog () {
      this.deliverDialog = true
    },
    onPropagateDialog () {
      this.confirm(false)
    },
    onClosePropagateDialog () {
      this.propagateDialog = false
    },
    onCloseDeliverDialog () {
      this.deliverDialog = false
      this.resetDeliverDialog()
    },
    async deliver () {
      const isValid = this.$refs.nip.validate()
      if (isValid) {
        this.overlay = true
        try {
          const payload = this.deliverForm
          const { response } = await this.orderModel.receiveWithCode(payload)
          this.detail = response.data
          this.$emit('update', new Order(this.detail))
          this.onCloseDeliverDialog()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    async attachInvoice () {
      this.attachDialog = true
    },
    async closeAttachDialog () {
      await this.load()
      this.attachDialog = false
      this.$emit('update', new Order(this.detail))
    },
    printOrder () {
      const copyTable = this.table.data
      copyTable.map(function (item) {
        if (item.choice) {
          item.choice.brand = item.choice.brand || ''
          item.choice.description = item.choice.description || ''
        }
        return item
      })
      const printableJson = copyTable

      print({
        printable: printableJson,
        properties: [
          { field: 'number', displayName: '' },
          { field: 'name', displayName: 'Producto' },
          { field: 'choice.description', displayName: 'Descripción' },
          { field: 'choice.brand', displayName: 'Marca' },
          { field: 'quantityRequested', displayName: 'Cantidad solicitada' },
          { field: 'amountWithTax', displayName: 'Precio unitario' },
          { field: 'totalWithTax', displayName: 'Total' }
        ],
        type: 'json',
        header: `<div>Orden: ${this.detail.uuid}</div><div>Total: $${this.total}</div>`
      })
    },
    emitUpdate () {
      this.$emit('update', new Order(this.detail))
    },
    openEditVariantInOrder (product) {
      this.$refs.editVariantInOrder.open(this.detail, product)
    },
    openGenerateCreditNoteDialog: async function () {
      this.creditNoteOpen = true
    },
    async addCreditNoteInOrder () {
      await this.load()
      this.creditNoteOpen = false
      this.$emit('update', new Order(this.detail))
    },
    onChangeStatus: function () {
      this.changeStatusDialog = true
    },
    onCloseChangeStatus: function () {
      this.changeStatusDialog = false
    },
    async changeStatusOrder () {
      this.overlay = true
      const payload = { restaurant: this.detail.restaurant._id || this.detail.restaurant }
      try {
        const response = await this.$api.put(`/v1/orders/${this.detail._id}/change-status`, payload)
        this.detail = response
        this.$toasted.show(`La orden de compra ${this.detail.uuid} se cambió de estatus exitosamente.`, { duration: 4000 })
        this.onCloseChangeStatus()
        this.$emit('update', new Order(this.detail))
        this.closeDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    validateService (service) {
      !service ? this.servicesDialog.form.amount = 0 : this.servicesDialog.form.amount = service.amount
    },
    validateMotiveCancellation (value) {
      if (value !== '01') delete this.motiveCancellation.form.subtitution
    },
    getTooltipText (motive) {
      return this.descriptionsMotives(motive) || ''
    },
    descriptionsMotives (status) {
      return {
        '01': 'Cuando la factura contiene algún error en las cantidades, claves o cualquier otro dato y ya se ha emitido el comprobante que la sustituye, el cual deberá indicarse por medio del atributo substitution.',
        '02': 'Cuando la factura contiene algún error en las cantidades, claves o cualquier otro dato y no se requiere relacionar con otra factura.',
        '03': 'Cuando la venta o transacción no se concretó.',
        '04': 'Cuando se requiere cancelar una factura al público en general porque el cliente solicita su comprobante'
      }[status]
    },
    displayModificationText () {
      let text = '-'
      if (this.detail.status === 'sent') text = 'Enviada por : '
      if (this.detail.status === 'confirmed') text = 'Confirmada por : '
      if (this.detail.status === 'invoiced') text = 'Facturada por : '
      if (this.detail.status === 'invalid') text = 'Última modificación por : '
      if (['received', 'pending_payment', 'paid'].includes(this.detail.status)) text = 'Recibida por : '
      if (this.detail.status === 'canceled') text = 'Cancelada por : '
      return text
    },
    getLastModificationPerson () {
      if (this.detail.status === 'created') {
        return this.detail.author.email
      } else if (this.detail.statusChanges) {
        const statusChanges = this.detail.statusChanges.find(x => x.name === this.detail.status)
        if (statusChanges && statusChanges.updateBy && statusChanges.updateBy.email) {
          return statusChanges.updateBy.email
        }
      }
      return '-'
    },
    findCancelationMotive (motive) {
      const motives = ['wrong_provider_product', 'product_not_received', 'shortage', 'product_not_neeed_it', 'restaurant_cancelation', 'wrong_quantity_or_product', 'restaurant_is_not_a_client', 'out_delivery_zone_range']
      if (!motives.includes(motive)) return motive
      return {
        wrong_provider_product: 'El restaurante se equivocó en el proveedor / producto',
        product_not_received: 'No se entregó el producto',
        shortage: 'Desabasto',
        product_not_neeed_it: 'El restaurante ya no necesita el producto',
        restaurant_cancelation: 'El restaurante solicitó una cancelación',
        wrong_quantity_or_product: 'Hay un error en el pedido: cantidades y/o producto',
        restaurant_is_not_a_client: 'El restaurante no es mi cliente',
        out_delivery_zone_range: 'La zona del restaurante está fuera de mi rango de entrega'
      }[motive]
    }
  }
}
</script>
